import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

// Single source of truth for Super-Admin organisation module management.
// The module registry is ALWAYS server-provided (GET /admin/orgs/{oid}/modules → {modules, registry});
// this component never duplicates the registry or writes storage directly. Toggling an implemented
// module calls PUT /admin/orgs/{oid}/modules/{key} {enabled} and reflects the effective state on success.
export function ModulesManager({ orgs }) {
  const [oid, setOid] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async (id) => {
    setOid(id);
    setData(null);
    if (!id) return;
    try { setData((await api.get(`/admin/orgs/${id}/modules`)).data); }
    catch { toast.error("Failed to load modules"); }
  };

  const toggle = async (key, enabled) => {
    setBusy(key);
    try {
      await api.put(`/admin/orgs/${oid}/modules/${key}`, { enabled });
      setData((d) => ({ ...d, modules: { ...d.modules, [key]: enabled } }));
      toast.success(`${data.registry[key]?.name || key} ${enabled ? "enabled" : "disabled"}`);
    } catch {
      toast.error("Update failed");
    } finally {
      setBusy(null);
    }
  };

  // Preserve registry insertion order while grouping.
  const groups = data ? Object.entries(data.registry).reduce((acc, [key, meta]) => {
    (acc[meta.group] = acc[meta.group] || []).push([key, meta]);
    return acc;
  }, {}) : {};

  return (
    <div className="space-y-4" data-testid="modules-panel">
      <div>
        <h2 className="font-display text-xl font-bold text-slate-800">Organisation modules</h2>
        <p className="text-sm text-slate-500">Enable or disable optional modules per organisation. Access still requires the user's permission and site access.</p>
      </div>
      <select
        value={oid}
        onChange={(e) => load(e.target.value)}
        data-testid="modules-org-select"
        className="w-full max-w-md border border-slate-200 rounded-lg px-3 py-2 text-sm"
      >
        <option value="">Select an organisation…</option>
        {orgs.map((o) => <option key={o.org_id} value={o.org_id}>{o.name}</option>)}
      </select>

      {data && (
        <div className="space-y-5" data-testid="modules-list">
          {Object.entries(groups).map(([group, items]) => (
            <div key={group} data-testid={`module-group-${group}`}>
              <div className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">{group}</div>
              <div className="grid sm:grid-cols-2 gap-2">
                {items.map(([key, meta]) => {
                  const enabled = data.modules[key] !== false;
                  return (
                    <label
                      key={key}
                      data-testid={`module-row-${key}`}
                      className={`flex items-center justify-between border rounded-lg px-3 py-2 text-sm bg-white ${meta.implemented ? "cursor-pointer" : "opacity-60"}`}
                    >
                      <span className="flex flex-col">
                        <span className="font-medium text-slate-800">{meta.name}</span>
                        <span className="text-[11px] text-slate-400" data-testid={`module-state-${key}`}>
                          {meta.implemented ? (enabled ? "Enabled" : "Disabled") : "Coming soon"}
                        </span>
                      </span>
                      <input
                        type="checkbox"
                        data-testid={`module-toggle-${key}`}
                        checked={enabled}
                        disabled={!meta.implemented || busy === key}
                        onChange={(e) => toggle(key, e.target.checked)}
                        className="accent-brand h-4 w-4"
                      />
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
